/**
 * @author Michael Hasenstein <hasenstein@yahoo.com>
 * @copyright REFINIO GmbH 2017
 * @license CC-BY-NC-SA-2.5; portions MIT License
 * @version 0.0.1
 */
import { createError } from './errors.js';
import { hasRecipe, isVersionedObjectType } from './object-recipes.js';
import { STORAGE } from './storage-base-common.js';
import { getIdHash } from './storage-id-hash-cache.js';
import { readUTF8TextFile } from './system/storage-base.js';
import { createLruMap } from './util/lru-map.js';
import { serializeWithType } from './util/promise.js';
import { looksLikeHash } from './util/type-checks.js';
import { getCurrentVersionNode } from './storage-versioned-objects.js';
/**
 * Backend function for {@link getOnlyLatestReferencingObjsHashAndId} and
 * {@link getOnlyLatestReferencingObjsHash}.
 *
 * It produces different return types depending on the boolean parameter.
 *
 * Cleaner code would have been to always return the complex type, and have a second frontend
 * function select just the hash for the simple hash return type case. However, that would
 * needlessly create a lot of objects, so I chose to use this ternary-operator approach that uses
 * an extra function and an extra parameter, even though especially the latter usually is
 * something to be avoided and replaced by having different functions instead of one parameter
 * that switches the return type.
 * @private
 * @param {SHA256Hash<HashTypes> | SHA256IdHash} targetHash
 * @param {T} typeOfReferencingObj
 * @param {number} createdAfter
 * @param {boolean} [justHash=true]
 * @returns {Promise<Array<SHA256Hash|HashAndIdHashAndTimestamp>>}
 */
async function _getOnlyLatestReferencingObjs(targetHash, typeOfReferencingObj, createdAfter = 0, justHash = true) {
    if (!isVersionedObjectType(typeOfReferencingObj)) {
        throw createError('RMQ-AVMC1', { targetHash, typeOfReferencingObj });
    }
    const entries = await getAllEntries(targetHash, typeOfReferencingObj);
    const acc = [];
    const vMapLookupCache = createLruMap(50);
    while (entries.length > 0) {
        // SERIAL EXECUTION in constant small steps - no use issuing too much I/O
        await Promise.all(entries.splice(-4).map(async (hashOfReferencingObj) => {
            const idHash = await getIdHash(hashOfReferencingObj);
            let entry = vMapLookupCache.get(idHash);
            if (entry === undefined) {
                const node = await getCurrentVersionNode(idHash);
                entry = {
                    hash: node.obj.data,
                    timestamp: node.timestamp ?? node.obj.creationTime
                };
            }
            vMapLookupCache.set(idHash, entry);
            if (entry.timestamp > createdAfter && hashOfReferencingObj === entry.hash) {
                acc.push(justHash
                    ? hashOfReferencingObj
                    : {
                        hash: hashOfReferencingObj,
                        idHash,
                        timestamp: entry.timestamp
                    });
            }
        }));
    }
    return acc;
}
/**
 * Frontend to {@link reverse-map-query.module:ts.getAllEntries|getAllEntries} which only returns
 * entries that point to the most current version of a referencing object.
 *
 * This function is only useful if the reverse map is for referencing objects that are
 * *versioned*. If it is called for an unversioned referencing object type, where the
 * reverse map has no entries for ID hashes (the 2nd hash is empty), it throws an `Error`.
 *
 * The function takes the results returned by
 * {@link reverse-map-query.module:ts.getAllEntries|reverse-map-query.getAllEntries} and uses the
 * referencing object ID hashes (2nd reverse map column) to load the version maps for those
 * objects. It then checks if any of the concrete version hashes for each ID hash is the most
 * current version of that object. It returns only those concrete object hashes for which this
 * is the case.
 *
 * The original use case for this function is checking access rights: Both {@link Access} and
 * {@link Group} objects are versioned, and both only grant access through the latest version.
 * If a {@link Person} once was member of a group but is not a member in the latest version of
 * the group they should not get access. Similar for Access objects which revoke previously
 * granted access to a ONE object.
 * @static
 * @async
 * @param {SHA256Hash|SHA256IdHash} targetHash - Object owning the reverse map to query. **For
 * versioned objects this must be an ID hash.**
 * @param {OneVersionedObjectTypeNames} typeOfReferencingObj - The specific reverse map from
 * `fromHash` back to any object of this type with a hash link to `idHash` or one of the
 * concrete versions
 * @param {number} [createdAfter] - Optional timestamp: The most current object is included
 * into the final result only if it has a version map timestamp *after* this time
 * @returns {Promise<HashAndIdHashAndTimestamp[]>} Returns an array of triples of hash, ID hash
 * and timestamp of the latest version of versioned objects referencing the object the reverse
 * map is for. **Note** that this function guarantees that there always are ID hashes, since it
 * throws an `Error` if invoked for unversioned object type reverse maps.
 * @throws {Error} Throws an error if the given 2nd parameter `toType` is not a versioned object
 * type (only versioned objects can have a "most current" version)
 */
export async function getOnlyLatestReferencingObjsHashAndId(targetHash, typeOfReferencingObj, createdAfter = 0) {
    return await _getOnlyLatestReferencingObjs(targetHash, typeOfReferencingObj, createdAfter, false);
}
/**
 * Same as {@link getOnlyLatestReferencingObjsHashAndId} but only returns the hashes and not the
 * ID hashes and timestamps.
 * @static
 * @async
 * @param {SHA256Hash|SHA256IdHash} targetHash - Object owning the reverse map to query. **For
 * versioned objects this must be an ID hash.**
 * @param {OneVersionedObjectTypeNames} typeOfReferencingObj - The specific reverse map from
 * `fromHash` back to any object of this type with a hash link to `idHash` or one of the
 * concrete versions
 * @param {number} [createdAfter] - Optional timestamp: The most current object is included
 * into the final result only if it has a version map timestamp *after* this time
 * @returns {Promise<SHA256Hash[]>} Returns an array of triples of hash, ID hash
 * and timestamp of the latest version of versioned objects referencing the object the reverse
 * map is for. **Note** that this function guarantees that there always are ID hashes, since it
 * throws an `Error` if invoked for unversioned object type reverse maps.
 * @throws {Error} Throws an error if the given 2nd parameter `toType` is not a versioned object
 * type (only versioned objects can have a "most current" version)
 */
export async function getOnlyLatestReferencingObjsHash(targetHash, typeOfReferencingObj, createdAfter = 0) {
    return await _getOnlyLatestReferencingObjs(targetHash, typeOfReferencingObj, createdAfter, true);
}
/**
 * Backend function for {@link getAllEntries}.
 * @private
 * @param {string} mapName
 * @returns {Promise<SHA256Hash[]>}
 */
async function _getAllEntries(mapName) {
    const isHash = looksLikeHash(mapName.slice(0, 64));
    const hasObject = mapName.slice(64, 72) === '.Object.';
    const isValidType = hasRecipe(mapName.slice(72));
    if (!isHash || !hasObject || !isValidType) {
        throw createError('RMQ-AE1', {
            mapName,
            isHash,
            hasObject,
            isValidType,
            type: mapName.slice(72)
        });
    }
    let mapData;
    try {
        mapData = await readUTF8TextFile(mapName, STORAGE.RMAPS);
    }
    catch (err) {
        if (err.name === 'FileNotFoundError') {
            return [];
        }
        throw err;
    }
    // slice() removes the final newline character
    return mapData.slice(0, -1).split('\n');
}
/**
 * Backend function for {@link getAllIdObjectEntries}.
 * @private
 * @param {string} mapName
 * @returns {Promise<SHA256Hash[]>}
 */
async function _getAllIdObjectEntries(mapName) {
    const isHash = looksLikeHash(mapName.slice(0, 64));
    const hasIdObject = mapName.slice(64, 74) === '.IdObject.';
    const isValidType = hasRecipe(mapName.slice(74));
    if (!isHash || !hasIdObject || !isValidType) {
        throw createError('RMQ-AE2', {
            mapName,
            isHash,
            hasIdObject,
            isValidType,
            type: mapName.slice(74)
        });
    }
    let mapData;
    try {
        mapData = await readUTF8TextFile(mapName, STORAGE.RMAPS);
    }
    catch (err) {
        if (err.name === 'FileNotFoundError') {
            return [];
        }
        throw err;
    }
    // slice() removes the final newline character
    return mapData.slice(0, -1).split('\n');
}
/**
 * Reads the given reverse map and returns the data in a 1:N hash => hashes form.
 *
 * A non-existent map file is not an error, it simply means no object references the one the
 * given reverse map is for.
 *
 * The given reverse map filename is checked against the expected pattern for these files, which is
 * Hash-of-object-the-map-is-for.Object.ReferencingOneObjectTypeName
 * and an `Error` is thrown if the pattern does not fit.
 * @static
 * @async
 * @param {SHA256Hash|SHA256IdHash} targetHash - Object owning the reverse map to query. **For
 * versioned objects this must be an ID hash.**
 * @param {OneVersionedObjectTypeNames} typeOfReferencingObj - The specific reverse map from
 * `idHash` back to any object of this type with a hash link to `idHash` or one of the concrete
 * versions
 * @returns {Promise<ReverseMapEntry[]>}
 */
export function getAllEntries(targetHash, typeOfReferencingObj) {
    const mapName = targetHash + '.Object.' + typeOfReferencingObj;
    return serializeWithType('ReverseMap ' + mapName, () => _getAllEntries(mapName));
}
/**
 * Obtains the hashes of all IdObjects that reference the targetHash.
 *
 *
 *
 * A non-existent map file is not an error, it simply means no object references the one the
 * given reverse map is for.
 *
 * The given reverse map filename is checked against the expected pattern for these files, which is
 * Hash-of-object-the-map-is-for.IdObject.ReferencingOneObjectTypeName
 * and an `Error` is thrown if the pattern does not fit.
 * @static
 * @async
 * @param {SHA256Hash|SHA256IdHash} targetHash - Object owning the reverse map to query. **For
 * versioned objects this must be an ID hash.**
 * @param {OneVersionedObjectTypeNames} typeOfReferencingObj - The specific reverse map from
 * `idHash` back to any object of this type with a hash link to `idHash` or one of the concrete
 * versions
 * @returns {Promise<ReverseMapEntry[]>}
 */
export function getAllIdObjectEntries(targetHash, typeOfReferencingObj) {
    const mapName = targetHash + '.IdObject.' + typeOfReferencingObj;
    return serializeWithType('ReverseMapId ' + mapName, () => _getAllIdObjectEntries(mapName));
}
//# sourceMappingURL=reverse-map-query.js.map